import { z } from "zod";
import { DeviceType, CommunicationType } from "./device";

// ---------------------------------------------------------------------------
// DeviceTypeProfile — shared contract between platform, frontend, and simulator
//
// Code-defined in @conatus/contracts as TypeScript constants validated by Zod.
// Source of truth ships with releases; no DB-edited profiles in MVP.
// ---------------------------------------------------------------------------

export const DataQuality = z.enum(["Good", "Stale", "Uncertain", "Bad"]);
export type DataQuality = z.infer<typeof DataQuality>;

export const LocationBehavior = z.enum(["Static", "Mobile", "Drifts"]);
export type LocationBehavior = z.infer<typeof LocationBehavior>;

/**
 * How long a command dispatch is expected to take end-to-end.
 * Immediate: <1 s (e.g. cloud relay)
 * Fast: <10 s (e.g. LTE turnaround)
 * Standard: <60 s (e.g. satellite uplink)
 * Slow: >60 s (e.g. store-and-forward, scheduled maintenance window)
 */
export const CommandLatencyClass = z.enum(["Immediate", "Fast", "Standard", "Slow"]);
export type CommandLatencyClass = z.infer<typeof CommandLatencyClass>;

/**
 * Where this device sits in a gateway-child topology.
 * Standalone: directly connected, no gateway relationship.
 * Gateway: aggregates telemetry from child devices.
 * Child: routes through a parent gateway.
 */
export const TopologyRole = z.enum(["Standalone", "Gateway", "Child"]);
export type TopologyRole = z.infer<typeof TopologyRole>;

const MetricDefinition = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("numeric"),
    name: z.string().min(1),
    unit: z.string().min(1),
    /** Inclusive [min, max] range for valid values. */
    range: z.tuple([z.number(), z.number()]),
    retentionDays: z.number().int().min(1).optional(),
  }),
  z.object({
    kind: z.literal("enum"),
    name: z.string().min(1),
    /** Allowed string values for this metric. */
    values: z.array(z.string().min(1)).min(1),
    retentionDays: z.number().int().min(1).optional(),
  }),
]);
export type MetricDefinition = z.infer<typeof MetricDefinition>;

export const DeviceTypeProfile = z.object({
  profileId: z.string().min(1),
  deviceType: DeviceType,
  communicationType: CommunicationType,
  /** Typical seconds between data samples for this device/comms combination. */
  expectedSampleIntervalSeconds: z.number().int().min(1),
  /** Seconds after lastSeenAt before data is considered Stale. */
  staleAfterSeconds: z.number().int().min(1),
  /** Telemetry metrics emitted by this device type. */
  metrics: z.array(MetricDefinition).min(1),
  /** Provider-side fault code strings this device may emit. */
  faultCodes: z.array(z.string().min(1)).default([]),
  /** Alert types this device type may raise. */
  supportedAlerts: z.array(z.string().min(1)).default([]),
  /** Command types the platform may dispatch to this device type. */
  supportedCommands: z.array(z.string().min(1)).default([]),
  locationBehavior: LocationBehavior,

  // -------------------------------------------------------------------------
  // Capability flags — define what this device class can do beyond telemetry
  // -------------------------------------------------------------------------

  /** Device produces video streams (still images or live). */
  supportsVideo: z.boolean().default(false),
  /** Device supports on-demand live stream requests. */
  supportsVideoStreaming: z.boolean().default(false),
  /** Device accepts RequestDiagnostics and returns structured diagnostic payload. */
  supportsDiagnostics: z.boolean().default(false),
  /** Device reports firmware version and update eligibility in telemetry. */
  supportsFirmwareInventory: z.boolean().default(false),
  /** Device accepts OTA firmware update commands. */
  supportsOtaUpdate: z.boolean().default(false),
  /**
   * Device can be issued a platform-managed mTLS certificate for direct
   * device-to-platform connectivity (future device-direct ingestion path).
   */
  supportsDirectCert: z.boolean().default(false),
  /**
   * Gateway-child topology role. Standalone = no topology, Gateway = aggregates
   * children, Child = routes through a parent gateway.
   */
  topologyRole: TopologyRole.default("Standalone"),
  /**
   * Expected command dispatch latency class for UI and timeout defaults.
   */
  commandLatencyClass: CommandLatencyClass.default("Standard"),
  /**
   * Which network identity namespaces this profile typically carries.
   * Used to surface the right identity fields in operator UI.
   * Values are NetworkIdentityNamespace enum keys (lowercase).
   * e.g. ["imei", "iccid"] for cellular, ["deveui"] for LoRa.
   */
  networkIdentityNamespaces: z.array(z.string().min(1)).default([]),

  /** Human-readable description shown in admin UI. */
  description: z.string().optional(),
});
export type DeviceTypeProfile = z.infer<typeof DeviceTypeProfile>;

// ---------------------------------------------------------------------------
// Profile-driven freshness helper
// ---------------------------------------------------------------------------

/**
 * Derive DataQuality from lastSeenAt and the profile stale threshold.
 * Returns "Good" when data is fresh, "Stale" when beyond the threshold,
 * and "Unknown" when lastSeenAt is absent.
 */
export function calculateDataQuality(
  lastSeenAt: string | null | undefined,
  profile: DeviceTypeProfile,
): DataQuality {
  if (!lastSeenAt) return "Uncertain";
  const ageMs = Date.now() - new Date(lastSeenAt).getTime();
  if (ageMs < 0) return "Uncertain";
  return ageMs / 1000 > profile.staleAfterSeconds ? "Stale" : "Good";
}

// ---------------------------------------------------------------------------
// Catalog constants — initial three profiles for MVP
// ---------------------------------------------------------------------------

export const DEVICE_PROFILES: readonly DeviceTypeProfile[] = [
  {
    profileId: "generic.v1",
    deviceType: "Other",
    communicationType: "Other",
    expectedSampleIntervalSeconds: 60,
    staleAfterSeconds: 300,
    metrics: [
      { kind: "numeric", name: "batteryLevel", unit: "%", range: [0, 100] },
      { kind: "numeric", name: "signalStrength", unit: "%", range: [0, 100] },
    ],
    faultCodes: [],
    supportedAlerts: [],
    supportedCommands: [],
    locationBehavior: "Static",
    supportsVideo: false,
    supportsVideoStreaming: false,
    supportsDiagnostics: false,
    supportsFirmwareInventory: false,
    supportsOtaUpdate: false,
    supportsDirectCert: false,
    topologyRole: "Standalone",
    commandLatencyClass: "Standard",
    networkIdentityNamespaces: [],
    description: "Generic fallback profile — no metric or command restrictions.",
  },
  {
    profileId: "camera.cellular.v1",
    deviceType: "Camera",
    communicationType: "Cellular",
    expectedSampleIntervalSeconds: 5,
    staleAfterSeconds: 30,
    metrics: [
      { kind: "numeric", name: "batteryLevel", unit: "%", range: [0, 100], retentionDays: 30 },
      { kind: "numeric", name: "signalStrength", unit: "%", range: [0, 100], retentionDays: 30 },
      { kind: "enum", name: "videoStreamHealth", values: ["OK", "Degraded", "Down"], retentionDays: 30 },
    ],
    faultCodes: ["STREAM_LOSS", "BATT_LOW", "NETWORK_ERROR"],
    supportedAlerts: ["Offline", "LowBattery", "StreamLoss", "Tamper"],
    supportedCommands: ["RebootDevice", "RequestStream", "RequestDiagnostics", "ClearFaultCode"],
    locationBehavior: "Static",
    supportsVideo: true,
    supportsVideoStreaming: true,
    supportsDiagnostics: true,
    supportsFirmwareInventory: true,
    supportsOtaUpdate: false,
    supportsDirectCert: false,
    topologyRole: "Standalone",
    commandLatencyClass: "Fast",
    networkIdentityNamespaces: ["imei", "iccid"],
    description: "Cellular-connected IP camera with live stream health reporting.",
  },
  {
    profileId: "lora.environmental.v1",
    deviceType: "Sensor",
    communicationType: "Lora",
    expectedSampleIntervalSeconds: 60,
    staleAfterSeconds: 600,
    metrics: [
      { kind: "numeric", name: "batteryLevel", unit: "%", range: [0, 100], retentionDays: 90 },
      { kind: "numeric", name: "signalStrength", unit: "%", range: [0, 100], retentionDays: 30 },
      { kind: "numeric", name: "temperatureC", unit: "°C", range: [-40, 85], retentionDays: 90 },
      { kind: "numeric", name: "humidityPct", unit: "%", range: [0, 100], retentionDays: 90 },
    ],
    faultCodes: ["BATT_LOW", "SENSOR_FAULT", "RADIO_ERROR"],
    supportedAlerts: ["Offline", "LowBattery", "WeakSignal", "TemperatureThreshold"],
    supportedCommands: ["RebootDevice", "RequestDiagnostics"],
    locationBehavior: "Static",
    supportsVideo: false,
    supportsVideoStreaming: false,
    supportsDiagnostics: true,
    supportsFirmwareInventory: false,
    supportsOtaUpdate: false,
    supportsDirectCert: false,
    topologyRole: "Standalone",
    commandLatencyClass: "Slow",
    networkIdentityNamespaces: ["deveui", "joineui"],
    description: "Low-power LoRa environmental sensor for temperature, humidity, and battery monitoring.",
  },
  {
    profileId: "satellite.gps.v1",
    deviceType: "Telematics",
    communicationType: "Satellite",
    expectedSampleIntervalSeconds: 900,
    staleAfterSeconds: 3600,
    metrics: [
      { kind: "numeric", name: "batteryLevel", unit: "%", range: [0, 100], retentionDays: 90 },
      { kind: "numeric", name: "signalStrength", unit: "%", range: [0, 100], retentionDays: 30 },
      { kind: "numeric", name: "speedKph", unit: "km/h", range: [0, 300], retentionDays: 30 },
      { kind: "numeric", name: "headingDeg", unit: "°", range: [0, 360], retentionDays: 30 },
    ],
    faultCodes: ["GPS_LOST", "BATT_LOW", "SAT_NO_LOCK"],
    supportedAlerts: ["Offline", "LowBattery", "GeofenceExit", "GpsLost"],
    supportedCommands: ["RebootDevice", "RequestDiagnostics"],
    locationBehavior: "Mobile",
    supportsVideo: false,
    supportsVideoStreaming: false,
    supportsDiagnostics: true,
    supportsFirmwareInventory: true,
    supportsOtaUpdate: false,
    supportsDirectCert: false,
    topologyRole: "Standalone",
    commandLatencyClass: "Slow",
    networkIdentityNamespaces: ["imei", "iccid"],
    description: "Satellite-connected GPS telematics unit for mobile asset tracking.",
  },
  {
    profileId: "wifi.doorsensor.v1",
    deviceType: "DoorSensor",
    communicationType: "Wifi",
    expectedSampleIntervalSeconds: 10,
    staleAfterSeconds: 60,
    metrics: [
      { kind: "numeric", name: "batteryLevel", unit: "%", range: [0, 100], retentionDays: 30 },
      { kind: "numeric", name: "signalStrength", unit: "%", range: [0, 100], retentionDays: 30 },
      { kind: "enum", name: "doorState", values: ["Open", "Closed", "Unknown"], retentionDays: 90 },
    ],
    faultCodes: ["TAMPER", "BATT_LOW", "WIFI_ERROR"],
    supportedAlerts: ["Offline", "LowBattery", "Tamper", "UnexpectedOpen"],
    supportedCommands: ["RebootDevice", "RequestDiagnostics"],
    locationBehavior: "Static",
    supportsVideo: false,
    supportsVideoStreaming: false,
    supportsDiagnostics: true,
    supportsFirmwareInventory: false,
    supportsOtaUpdate: false,
    supportsDirectCert: false,
    topologyRole: "Standalone",
    commandLatencyClass: "Fast",
    networkIdentityNamespaces: ["mac"],
    description: "Wi-Fi door/access sensor reporting open/closed state and tamper events.",
  },
  {
    profileId: "bluetooth.temperature.v1",
    deviceType: "Sensor",
    communicationType: "Bluetooth",
    expectedSampleIntervalSeconds: 30,
    staleAfterSeconds: 180,
    metrics: [
      { kind: "numeric", name: "batteryLevel", unit: "%", range: [0, 100], retentionDays: 30 },
      { kind: "numeric", name: "temperatureC", unit: "°C", range: [-30, 60], retentionDays: 90 },
    ],
    faultCodes: ["BATT_LOW", "SENSOR_FAULT"],
    supportedAlerts: ["Offline", "LowBattery", "TemperatureThreshold"],
    supportedCommands: ["RebootDevice"],
    locationBehavior: "Static",
    supportsVideo: false,
    supportsVideoStreaming: false,
    supportsDiagnostics: false,
    supportsFirmwareInventory: false,
    supportsOtaUpdate: false,
    supportsDirectCert: false,
    topologyRole: "Standalone",
    commandLatencyClass: "Standard",
    networkIdentityNamespaces: ["mac"],
    description: "Bluetooth temperature sensor for cold-chain monitoring.",
  },
] as const;

// ---------------------------------------------------------------------------
// Lookup helpers
// ---------------------------------------------------------------------------

export function getProfile(profileId: string): DeviceTypeProfile | null {
  return (DEVICE_PROFILES as readonly DeviceTypeProfile[]).find(
    (p) => p.profileId === profileId,
  ) ?? null;
}

export function getProfileOrDefault(profileId: string | null | undefined): DeviceTypeProfile {
  if (profileId) {
    const found = getProfile(profileId);
    if (found) return found;
  }
  return (DEVICE_PROFILES as readonly DeviceTypeProfile[])[0]!;
}

/** Map from deviceType+communicationType to canonical profileId. */
export const DEVICE_PROFILE_MAP: Readonly<Record<string, string>> = {
  "Camera:Cellular": "camera.cellular.v1",
  "Camera:Wifi": "camera.cellular.v1",
  "Sensor:Lora": "lora.environmental.v1",
  "Sensor:Bluetooth": "bluetooth.temperature.v1",
  "Sensor:Other": "lora.environmental.v1",
  "Telematics:Satellite": "satellite.gps.v1",
  "DoorSensor:Wifi": "wifi.doorsensor.v1",
  "DoorSensor:Bluetooth": "wifi.doorsensor.v1",
};

export function profileIdFromDeviceType(
  deviceType: string,
  communicationType: string,
): string {
  return DEVICE_PROFILE_MAP[`${deviceType}:${communicationType}`] ?? "generic.v1";
}
